import { DateTime } from 'luxon';
import { hasPlanAccess } from '@/common/access';
import { applyTimezoneOffset } from '@/common/timezone/timezone.helper';
import { DocumentViewConstants } from '@/document-view/constants/document-view';
import { EnumAppMode } from '@/enums/AppMode';
import EnumDocumentStatus from '@/enums/DocumentStatus';
import { EnumPlans } from '@/enums/Plans';
import { EDocumentPageType } from '@lib-shared/enums/document-page-type';
import { IDocument } from '@lib-shared/types/Document';
import { IDocumentPage } from '@lib-shared/types/document-page';
import { EDocumentType } from '@lib-shared/enums/document-type';
import { IOptionMenuItem } from '@/options-menu/types/option-menu-item';
import { EOptionMenuDocumentViewCallback } from '@/options-menu/types/enums/option-menu-item-callback';
import { IDocumentRevision, EDocumentRevisionStatus } from '@lib-shared/types/document-revision';

export class DocumentViewHelper {
  static getOptionFromList(name: EOptionMenuDocumentViewCallback): IOptionMenuItem {
    return DocumentViewConstants.availableOptions.find(option => option.callback === name);
  }

  static sortOptions(options: IOptionMenuItem[]): IOptionMenuItem[] {
    return options.sort((optionOne, optionTwo) => optionOne.index - optionTwo.index);
  }

  static getSignedOptions(
    document: IDocument,
    hasFields: boolean,
    appMode: EnumAppMode
  ): IOptionMenuItem[] {
    const options: IOptionMenuItem[] = [];
    const salesMode = appMode !== EnumAppMode.Business || document.type === EDocumentType.Sales;
    options.push(
      this.getOptionFromList(EOptionMenuDocumentViewCallback.Preview),
      this.getOptionFromList(EOptionMenuDocumentViewCallback.ChangeDocumentOwner),
      this.getOptionFromList(EOptionMenuDocumentViewCallback.CreateNewVersion),
      this.getOptionFromList(EOptionMenuDocumentViewCallback.DeleteDocument),
      this.getOptionFromList(EOptionMenuDocumentViewCallback.Download)
    );

    if (hasFields) {
      options.push(this.getOptionFromList(EOptionMenuDocumentViewCallback.ExportFieldData));
    }

    if (salesMode) {
      options.push(this.getOptionFromList(EOptionMenuDocumentViewCallback.MarkAsLost));
    }

    return this.sortOptions(options);
  }

  static getSentOptions(
    document: IDocument,
    appMode: EnumAppMode,
    securitySms: boolean
  ): IOptionMenuItem[] {
    const options: IOptionMenuItem[] = [];
    const salesMode = appMode !== EnumAppMode.Business || document.type === EDocumentType.Sales;
    const signingWithSMSDisabled = securitySms === false || !hasPlanAccess(EnumPlans.business);
    const { isSigning } = document;
    options.push(
      this.getOptionFromList(EOptionMenuDocumentViewCallback.Preview),
      this.getOptionFromList(EOptionMenuDocumentViewCallback.SendVideoReminder),
      this.getOptionFromList(EOptionMenuDocumentViewCallback.SendReminderEmail),
      this.getOptionFromList(EOptionMenuDocumentViewCallback.AddNewRecipient),
      this.getOptionFromList(EOptionMenuDocumentViewCallback.Download),
      this.getOptionFromList(EOptionMenuDocumentViewCallback.RecallDeal),
      this.getOptionFromList(EOptionMenuDocumentViewCallback.CreateNewVersion)
    );

    if (document.sendDate) {
      options.push(this.getOptionFromList(EOptionMenuDocumentViewCallback.UpcomingActivities));
    }
    if (isSigning && !signingWithSMSDisabled) {
      options.push(this.getOptionFromList(EOptionMenuDocumentViewCallback.SendSMS));
    }

    if (isSigning && salesMode) {
      options.push(this.getOptionFromList(EOptionMenuDocumentViewCallback.MarkAsLost));
    }

    if (salesMode && isSigning) {
      options.push(this.getOptionFromList(EOptionMenuDocumentViewCallback.UploadSignedDocument));
    } else if (salesMode && !isSigning) {
      options.push(this.getOptionFromList(EOptionMenuDocumentViewCallback.FlagAsWon));
    }

    return this.sortOptions(options);
  }

  static getDefaultOptions(document: IDocument): IOptionMenuItem[] {
    const options: IOptionMenuItem[] = [];

    if (document.status === EnumDocumentStatus.scheduled) {
      options.push(this.getOptionFromList(EOptionMenuDocumentViewCallback.Reschedule));
    }

    if (document.status === EnumDocumentStatus.recalled) {
      options.push(this.getOptionFromList(EOptionMenuDocumentViewCallback.DeleteDocument));
    } else {
      options.push(this.getOptionFromList(EOptionMenuDocumentViewCallback.RecallDeal));
      options.push(this.getOptionFromList(EOptionMenuDocumentViewCallback.Preview));
    }

    options.push(this.getOptionFromList(EOptionMenuDocumentViewCallback.CreateNewVersion));

    return this.sortOptions(options);
  }

  static options(
    document: IDocument,
    hasFields: boolean,
    appMode: EnumAppMode,
    securitySms: boolean
  ): IOptionMenuItem[] {
    const documentIsSigned =
      document.status === EnumDocumentStatus.signed ||
      document.status === EnumDocumentStatus.signedwithoutverification;
    const documentRecalledRejectedLostScheduledDraft =
      document.status === EnumDocumentStatus.recalled ||
      document.status === EnumDocumentStatus.rejected ||
      document.status === EnumDocumentStatus.lost ||
      document.status === EnumDocumentStatus.draft ||
      document.status === EnumDocumentStatus.scheduled;

    if (documentIsSigned) {
      return this.getSignedOptions(document, hasFields, appMode);
    }

    //TODO: check if the date is the correct check and not new Date(data.document.send_date)
    if (
      DateTime.fromISO(applyTimezoneOffset(document.sendDate, true)).toUTC().toLocaleString({
        day: 'numeric',
        year: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      }) !== 'Invalid Date' &&
      !documentRecalledRejectedLostScheduledDraft
    ) {
      return this.getSentOptions(document, appMode, securitySms);
    } else {
      return this.getDefaultOptions(document);
    }
  }

  static hasPDFPages(document: IDocument): boolean {
    return (
      !!document.pages?.length &&
      document.pages.some(
        (page: IDocumentPage) => page.pageType === EDocumentPageType.DocumentContent
      )
    );
  }

  static getIsEditingSupported(document: IDocument): boolean {
    if (!document?.documentRevisions) {
      return false;
    }
    const nonSignableRevision: IDocumentRevision = document.documentRevisions.find(
      (revision: IDocumentRevision) => revision.status === EDocumentRevisionStatus.NonSignable
    );

    const sealedRevision: IDocumentRevision = document.documentRevisions.find(
      (revision: IDocumentRevision) => revision.status === EDocumentRevisionStatus.Sealed
    );
    return !!nonSignableRevision && nonSignableRevision.revision > (sealedRevision?.revision || 0);
  }

  static isExpired(date: string): boolean {
    const time: string = applyTimezoneOffset(date, false);
    const expirationDate: DateTime = DateTime.fromISO(time, { zone: 'utc' });
    const currentDate: DateTime = DateTime.now();
    return currentDate > expirationDate;
  }
}
